/**
 * ProcessingStep - Step final que executa o EntityTreeProcessor e mostra progresso.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, LinearProgress, List, ListItem, ListItemIcon, ListItemText,
  CircularProgress, Alert, Button, Divider, Paper, Tooltip, IconButton,
} from '@mui/material';
import {
  Check as CheckIcon,
  Warning as WarningIcon,
  Celebration as CelebrationIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import processEntityTree from '../../../services/entityTreeProcessor';

export default function ProcessingStep({
  entityTree,
  onProcessingStart,
  onProcessingEnd,
  onComplete,
  onFinish,
}) {
  const [progress, setProgress] = useState(0);
  const [statusList, setStatusList] = useState([]);
  const [error, setError] = useState(null);
  const [complete, setComplete] = useState(false);
  const [result, setResult] = useState(null);
  const startedRef = useRef(false);

  // Iniciar processamento automaticamente ao montar
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      onProcessingStart?.();

      const handleProgress = (current, total, message, status) => {
        const pct = total > 0 ? (current / total) * 100 : 0;
        setProgress(pct);

        if (status === 'running') {
          setStatusList(prev => [...prev, { text: message, done: false }]);
        } else {
          setStatusList(prev => {
            const updated = [...prev];
            if (updated.length > 0) {
              updated[updated.length - 1] = {
                text: message,
                done: true,
                warning: status === 'warning',
                success: status === 'success',
              };
            }
            return updated;
          });
        }
      };

      const result = await processEntityTree(entityTree.tree, handleProgress);

      if (result.success) {
        setProgress(100);
        setComplete(true);
        setResult(result);
        setStatusList(prev => [...prev, { text: 'Tudo pronto!', done: true, success: true }]);
        onComplete?.();
      } else {
        setError(result.error);
      }

      onProcessingEnd?.();
    };

    run();
  }, []);

  const copyScheduleLink = (schedule) => {
    const link = `https://share.agendario.com.br/schedule/${schedule.id}`;
    navigator.clipboard.writeText(link);
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Header */}
      {complete ? (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CelebrationIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" color="success.main" gutterBottom>
            Configuração Concluída!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Todas as entidades e vínculos foram criados com sucesso.
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="error" gutterBottom>
            Erro na Criação
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Criando...
          </Typography>
        </Box>
      )}

      {/* Progress bar */}
      {!error && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 10, borderRadius: 5 }}
            color={complete ? 'success' : 'primary'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Schedule links */}
      {complete && result?.createdSchedules?.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Links de compartilhamento:
          </Typography>
          {result.createdSchedules.map(schedule => (
            <Paper key={schedule.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {schedule.name || `Agenda de ${schedule.professionalName}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    https://share.agendario.com.br/schedule/{schedule.id}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Copiar link">
                    <IconButton onClick={() => copyScheduleLink(schedule)} size="small">
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  {navigator.share && (
                    <Tooltip title="Compartilhar">
                      <IconButton
                        size="small"
                        onClick={() => navigator.share({
                          title: schedule.name,
                          url: `https://share.agendario.com.br/schedule/${schedule.id}`,
                        })}
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </>
      )}

      {/* Status list */}
      <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
        {statusList.map((status, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {status.done ? (
                status.warning
                  ? <WarningIcon color="warning" fontSize="small" />
                  : <CheckIcon color={status.success ? 'success' : 'action'} fontSize="small" />
              ) : (
                <CircularProgress size={18} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={status.text}
              primaryTypographyProps={{
                color: status.success ? 'success.main' : 'text.primary',
                fontWeight: status.success ? 'bold' : 'normal',
                variant: 'body2',
              }}
            />
          </ListItem>
        ))}
      </List>

      {/* Finish button */}
      {(complete || error) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color={complete ? 'success' : 'error'}
            onClick={onFinish}
            size="large"
          >
            {complete ? 'Concluir' : 'Fechar'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
